import { useMutation } from '@tanstack/react-query';
// Service
import { postAuthToken, getPermissionsMap } from './parazolService';
// Parazol Context
import { useParazolAuthContext } from './ParazolContext';
// Core
import { hasPermission } from './core';

const useParazol = () => {
  // Hooks
  const [context, dispatch] = useParazolAuthContext();
  // API Calls
  /**
   * React Query Mutation to get an auth token then trigger
   * the logic to store it and make it available through the App.
   * @returns {import('@tanstack/react-query').MutationTuple<AuthObject, Error, object, unknown>}
   */
  const postAuthTokenMutation = useMutation(
    postAuthToken,
    {
      onSuccess: async (data) => {
        dispatch({ type: 'SET_AUTHENTICATION', payload: data });
        const permissionsResult = await getPermissionsMap();

        try {
          if (permissionsResult.data) {
            const permissionsObject = permissionsResult.data
              .reduce((acc, { microServiceId, permissions }) => {
                const newAcc = {
                  ...acc,
                  [microServiceId]: permissions,
                };
                return newAcc;
              }, {});
            dispatch({ type: 'SET_PERMISSIONS', payload: permissionsObject });
          }
        } catch (error) {
          dispatch({ type: 'DELETE_AUTHENTICATION' });
          throw new Error(error);
        }
      },
    },
  );
  /**
   * Check if connected user has all the permissions passed as argument.
   * @param {Array<string>} permissionsToCheck
   * @param {string} [entry='MAIN']
   * @returns {boolean}
   */
  const hasConnectedUserPermission = (permissionsToCheck, entry = 'MAIN') => (
    hasPermission(context.prm[entry], permissionsToCheck)
  );
  /**
   * Disconnect the connected user.
   * @returns {void}
   */
  const disconnectUser = () => {
    dispatch({ type: 'DELETE_AUTHENTICATION' });
  };

  return {
    authContext: context,
    hasPermission: hasConnectedUserPermission,
    postAuthTokenMutation,
    disconnectUser,
  };
};

export default useParazol;
